import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, visuallyHidden } from 'bonfire-kindling';

import { TAXONOMY_SETS } from 'constants/taxonomySets';
import Item from 'components/Item/Item';

/*
 * Display of a single taxonomy code including name, code, description, expand/collapse controls, and add/remove controls
 */
const TaxonomyCodeListItem = ({
  indent = 0,
  isExpanded = false,
  isShowingSelections,
  selectedTaxonomyCodes,
  selectedTaxonomySetID,
  setIsLoading,
  setIsShowingSelections,
  setSelectedTaxonomyCodes,
  setTaxonomyCodesBreadCrumbs,
  taxonomyCode,
  taxonomyCodesBreadCrumbs,
}) => {
  const { t } = useTranslation();

  /*
   * Close a taxonomy item's children
   */
  const collapse = useCallback(() => {
    setIsLoading(true);
    setTaxonomyCodesBreadCrumbs(
      taxonomyCodesBreadCrumbs.slice(0, taxonomyCodesBreadCrumbs.indexOf(taxonomyCode))
    );
  }, [setIsLoading, setTaxonomyCodesBreadCrumbs, taxonomyCode, taxonomyCodesBreadCrumbs]);

  /*
   * Open a taxonomy item to show its children
   */
  const expand = useCallback(() => {
    setIsLoading(true);
    setIsShowingSelections(false);
    setTaxonomyCodesBreadCrumbs(taxonomyCodesBreadCrumbs.concat(taxonomyCode));
  }, [
    setIsLoading,
    setIsShowingSelections,
    setTaxonomyCodesBreadCrumbs,
    taxonomyCode,
    taxonomyCodesBreadCrumbs,
  ]);

  /*
   * Determine if a code has an ancestor which has been selected.
   * This is used determine if the current code should be disabled from allowing adding/removing from selections,
   * matching user visuals to behind-the-scenes matching behaviour
   */
  const isAncestorSelected = useMemo(
    () =>
      selectedTaxonomyCodes
        .filter(selectedCode => selectedCode.taxonomyCodeID !== taxonomyCode.taxonomyCodeID)
        .filter(selectedCode => selectedCode.token1 === taxonomyCode.token1)
        .filter(selectedCode => selectedCode.token2 === taxonomyCode.token2 || !selectedCode.token2)
        .filter(selectedCode => selectedCode.token3 === taxonomyCode.token3 || !selectedCode.token3)
        .filter(selectedCode => selectedCode.token4 === taxonomyCode.token4 || !selectedCode.token4)
        .filter(selectedCode => selectedCode.token5 === taxonomyCode.token5 || !selectedCode.token5)
        .filter(selectedCode => selectedCode.token6 === taxonomyCode.token6 || !selectedCode.token6)
        .length > 0,
    [selectedTaxonomyCodes, taxonomyCode]
  );

  /*
   * Determine if a code is included in user's selections
   * A given code should be included if it or one of its ancestors has been manually selected by the user
   */
  const isSelected = useMemo(
    () =>
      selectedTaxonomyCodes
        .filter(selectedCode => selectedCode.token1 === taxonomyCode.token1)
        .filter(selectedCode => selectedCode.token2 === taxonomyCode.token2 || !selectedCode.token2)
        .filter(selectedCode => selectedCode.token3 === taxonomyCode.token3 || !selectedCode.token3)
        .filter(selectedCode => selectedCode.token4 === taxonomyCode.token4 || !selectedCode.token4)
        .filter(selectedCode => selectedCode.token5 === taxonomyCode.token5 || !selectedCode.token5)
        .filter(selectedCode => selectedCode.token6 === taxonomyCode.token6 || !selectedCode.token6)
        .length > 0,
    [selectedTaxonomyCodes, taxonomyCode]
  );

  /*
   * Add a taxonomy code to the vendor's selections. A given code will also implicitly include all
   * of its descendants, so we can remove any descendants already in the list
   */
  const selectedTaxonomyCodesAdd = useCallback(
    () =>
      setSelectedTaxonomyCodes(prevIncludedCodes => [
        ...prevIncludedCodes.filter(
          code =>
            !(
              (!!code.token2 && !taxonomyCode.token2 && code.token1 === taxonomyCode.token1) ||
              (!!code.token3 &&
                !taxonomyCode.token3 &&
                code.token1 === taxonomyCode.token1 &&
                code.token2 === taxonomyCode.token2) ||
              (!!code.token4 &&
                !taxonomyCode.token4 &&
                code.token1 === taxonomyCode.token1 &&
                code.token2 === taxonomyCode.token2 &&
                code.token3 === taxonomyCode.token3) ||
              (!!code.token5 &&
                !taxonomyCode.token5 &&
                code.token1 === taxonomyCode.token1 &&
                code.token2 === taxonomyCode.token2 &&
                code.token3 === taxonomyCode.token3 &&
                code.token4 === taxonomyCode.token4) ||
              (!!code.token6 &&
                !taxonomyCode.token6 &&
                code.token1 === taxonomyCode.token1 &&
                code.token2 === taxonomyCode.token2 &&
                code.token3 === taxonomyCode.token3 &&
                code.token4 === taxonomyCode.token4 &&
                code.token5 === taxonomyCode.token5)
            )
        ),
        taxonomyCode,
      ]),
    [setSelectedTaxonomyCodes, taxonomyCode]
  );

  /*
   * Remove a taxonomy code to the vendor's selections.  This will also implicitly remove all of this code's descendants
   */
  const selectedTaxonomyCodesRemove = useCallback(() => {
    setSelectedTaxonomyCodes(prevIncludedCodes => [
      ...prevIncludedCodes.filter(code => code.taxonomyCodeID !== taxonomyCode.taxonomyCodeID),
    ]);

    // we are removing the last code from our list, but selectedTaxonomyCodes hasn't actually been updated yet
    if (isShowingSelections && selectedTaxonomyCodes.length === 1) {
      setIsLoading(true);
      setIsShowingSelections(false);
      setTaxonomyCodesBreadCrumbs([]);
    }
  }, [
    isShowingSelections,
    selectedTaxonomyCodes.length,
    setIsLoading,
    setIsShowingSelections,
    setSelectedTaxonomyCodes,
    setTaxonomyCodesBreadCrumbs,
    taxonomyCode.taxonomyCodeID,
  ]);

  const isExpandable = useMemo(() => {
    switch (selectedTaxonomySetID) {
      case TAXONOMY_SETS.UNSPSC:
        return !(!isExpanded && (!!taxonomyCode.token4 || isShowingSelections));
      case TAXONOMY_SETS.NAICS:
        return !(!isExpanded && (!!taxonomyCode.token5 || isShowingSelections));
      default:
        break;
    }
  }, [isExpanded, selectedTaxonomySetID, taxonomyCode, isShowingSelections]);

  return (
    <Item
      isSelected={isSelected}
      isExpanded={isExpanded}
      isExpandable={isExpandable}
      onChange={e =>
        e.target.checked ? selectedTaxonomyCodesAdd() : selectedTaxonomyCodesRemove()
      }
      onExpandClick={() => (isExpanded ? collapse() : expand())}
      sx={{
        marginLeft: `${indent}rem`,
        paddingTop: 3,
        paddingBottom: 3,
      }}
      addLabel={t('settings.add_label', { title: taxonomyCode.name })}
      removeLabel={t('settings.remove_label', { title: taxonomyCode.name })}
      checkboxDisabled={isAncestorSelected}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            minWidth: { xs: '100px', sm: '200px', md: '300px' },
            marginRight: theme => theme.spacing(3),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {taxonomyCode.name}
          </Typography>
          <Typography sx={visuallyHidden}>{t('settings.commodity_codes.code_number')}</Typography>
          <Typography variant="body2">
            {
              // taxonomy codes should be displayed with '.' seperators between tokens
              [
                taxonomyCode.token1,
                taxonomyCode.token2,
                taxonomyCode.token3,
                taxonomyCode.token4,
                taxonomyCode.token5,
                taxonomyCode.token6,
              ]
                .filter(v => v)
                .join('.')
            }
          </Typography>
        </Box>
        <Box sx={{ marginRight: theme => theme.spacing(3) }}>
          <Typography variant="body2">{taxonomyCode.description}</Typography>
        </Box>
      </Box>
    </Item>
  );
};

export default TaxonomyCodeListItem;
